import {
    DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, TextField,
} from '@fluentui/react'
import React, {
    useCallback, useEffect, useRef, useState,
} from 'react'
import ParentCompany, { ErrorParentCompany } from 'requests/objects/parentCompany'
import Status from 'types/status'
import CancelRequestError from 'requests/errors/cancelRequestError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import ParentCompaniesHandler from 'requests/handlers/parentCompaniesHandler'
import resetCarret from 'helpers/methods/resetCarret'

/**
 * @param {object} props Props
 * @param {boolean} props.isVisible isVisible
 * @param {(isVisible: boolean) => void} props.setIsVisible setIsVisible
 * @param {(parentCompany: ParentCompany) => void} props.setParentCompanny setParentCompanny
 * @param {ParentCompaniesHandler} props.handler handler
 * @returns {JSX.Element} Element
 */
export default function NewParentCompanyModal({
    isVisible, setIsVisible, handler, setParentCompanny,
}) {
    const [name, setName] = useState('')
    const [status, setStatus] = useState(Status.IDLE)
    const [errorField, setErrorField] = useState(new ErrorParentCompany())

    /** @type {React.Ref<import('requests/apiHandler').RequestApi<ParentCompany>>} */
    const handlerCreate = useRef(null)

    const save = useCallback(async () => {
        try {
            handlerCreate.current = handler.create(new ParentCompany({ name }))
            const parentCompany = await handlerCreate.current.fetch()
            setIsVisible(false)
            setParentCompanny(parentCompany)
        } catch (error) {
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                    break
                case InvalidEntityError:
                    setErrorField(/** @type {InvalidEntityError<ErrorParentCompany>} */(error).errorField)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    setStatus(Status.REJECTED)
                    break
                default:
                    setStatus(Status.REJECTED)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    break
            }
        }
    }, [setIsVisible, handler, name, setParentCompanny])

    // On component did unmount
    useEffect(() => () => {
        handlerCreate.current?.cancel()
    }, [])

    useEffect(() => {
        if (!isVisible) {
            setName('')
            setStatus(Status.IDLE)
            setErrorField(new ErrorParentCompany())
            handlerCreate.current?.cancel()
        }
    }, [isVisible])

    return (
        <Dialog
            hidden={!isVisible}
            onDismiss={() => setIsVisible(false)}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Nouvelle société mère',
                subText: 'Voulez-vous sauvegarder la société mère?',
            }}
            modalProps={{
                isBlocking: true,
            }}
            maxWidth="555px"
            minWidth="33%"
        >
            <form
                onSubmit={ev => {
                    ev.preventDefault()
                    save()
                }}
            >
                <TextField
                    label="Nom"
                    disabled={status === Status.PENDING}
                    value={name}
                    onChange={(ev, newVal) => {
                        setName(newVal?.toUpperCase())
                        resetCarret(ev)
                    }}
                    errorMessage={errorField.name}
                />
                <DialogFooter>
                    <DefaultButton
                        onClick={() => setIsVisible(false)}
                        text="Annuler"
                    />
                    <PrimaryButton
                        text="Créer"
                        type="submit"
                        disabled={!name}
                    />
                </DialogFooter>
            </form>
        </Dialog>
    )
}
